import { useEffect, useRef, useState } from 'react'
import './OrderOtpModal.css'

const CODE_TTL_MS = 5 * 60 * 1000

function OrderOtpModal({
  isOpen,
  phone,
  sending,
  verifying,
  blocked,
  error,
  expiresAt,
  onClose,
  onResend,
  onVerify,
}) {
  const inputRef = useRef(null)
  const [code, setCode] = useState('')
  const [now, setNow] = useState(() => Date.now())

  useEffect(() => {
    setCode('')
  }, [isOpen, expiresAt])

  useEffect(() => {
    if (!isOpen || !expiresAt) return undefined
    setNow(Date.now())
    const id = window.setInterval(() => setNow(Date.now()), 250)
    return () => window.clearInterval(id)
  }, [isOpen, expiresAt])

  useEffect(() => {
    if (!isOpen || sending) return undefined
    const id = window.setTimeout(() => inputRef.current?.focus(), 0)
    return () => window.clearTimeout(id)
  }, [isOpen, sending, expiresAt])

  useEffect(() => {
    if (!isOpen) return undefined
    const onKeyDown = (event) => {
      if (event.key === 'Escape' && !verifying) onClose?.()
    }
    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [isOpen, verifying, onClose])

  if (!isOpen) return null

  const expiresAtMs = expiresAt ? new Date(expiresAt).getTime() : NaN
  const remainingMs = Number.isFinite(expiresAtMs) ? Math.max(0, expiresAtMs - now) : null
  const expired = remainingMs === 0
  const progress = remainingMs != null ? Math.min(1, remainingMs / CODE_TTL_MS) : null
  const waitingForCode = sending && !(remainingMs > 0)
  const canVerify = code.length === 4 && !waitingForCode && !verifying && !blocked && !expired

  const requestClose = () => {
    if (verifying) return
    onClose?.()
  }

  return (
    <div className="order-otp-overlay" onMouseDown={requestClose} role="presentation">
      <div
        className="order-otp-modal"
        onMouseDown={(event) => event.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label="Подтверждение заказа"
      >
        <div className="order-otp-header">
          <div className="order-otp-title">Подтверждение</div>
          <button
            className="order-otp-close"
            type="button"
            onClick={requestClose}
            disabled={verifying}
            aria-label="Закрыть"
          >
            ×
          </button>
        </div>

        <div className="order-otp-body">
          {sending && !(remainingMs > 0) ? (
            <p className="order-otp-text">
              {expiresAt ? 'Отправляем новый код…' : 'Отправляем код в WhatsApp…'}
            </p>
          ) : expiresAt ? (
            <p className="order-otp-text">
              Мы отправили код в WhatsApp на <span className="order-otp-phone">{phone}</span>
            </p>
          ) : (
            <p className="order-otp-text">
              Не удалось отправить код на <span className="order-otp-phone">{phone}</span>
            </p>
          )}

          <div className="order-otp-field">
            {progress != null && (
              <div
                className="order-otp-progress"
                role="progressbar"
                aria-valuemin={0}
                aria-valuemax={100}
                aria-valuenow={Math.round(progress * 100)}
                aria-label="Время действия кода"
              >
                <div className="order-otp-progress-fill" style={{ width: `${progress * 100}%` }} />
              </div>
            )}
            <input
              ref={inputRef}
              className="order-otp-input"
              value={code}
              onChange={(event) => setCode(event.target.value.replace(/\D/g, '').slice(0, 4))}
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={4}
              placeholder="0000"
              aria-label="Код подтверждения"
              disabled={waitingForCode || verifying || blocked}
            />
          </div>
          {expired && !sending && <p className="order-otp-expired">Срок действия кода истёк</p>}

          {error && <div className="order-otp-error">{error}</div>}

          <button
            className="order-otp-primary"
            type="button"
            disabled={!canVerify}
            onClick={() => onVerify?.(code)}
          >
            {verifying ? 'Создание заказа…' : 'Подтвердить'}
          </button>

          <button
            className="order-otp-resend"
            type="button"
            disabled={sending || verifying || blocked}
            onClick={() => onResend?.()}
          >
            {sending ? 'Отправка…' : 'Отправить код ещё раз'}
          </button>
        </div>
      </div>
    </div>
  )
}

export default OrderOtpModal
